import { useEffect, useState } from "react";
import type { WordDTO } from "@shared/types";
import { speak } from "../audio";
import { viAudioUrl } from "../../local/languageSwap";
import { GameImage } from "./GameImage";
import { useT } from "../../local/i18n";

interface Props {
  words: WordDTO[];
  onDone: () => void;
}

/**
 * Card flipper shown before a level starts. Each word card auto-speaks the
 * English on appear, lets the student tap to hear it again, and shows the
 * Vietnamese + example sentence. Swipe / tap "Next" to advance, or skip.
 */
export function WordIntro({ words, onDone }: Props) {
  const [i, setI] = useState(0);
  const word = words[i];
  const t = useT();

  useEffect(() => {
    if (!word) return;
    // Bilingual teach: English first, then Vietnamese ~1.7s later so the
    // student learns the meaning, not just the sound.
    const t1 = setTimeout(() => speak(word.english, { audioUrl: word.audioUrl, lang: "en-US" }), 200);
    const t2 = word.vietnamese && word.vietnamese.trim()
      ? setTimeout(() => speak(word.vietnamese, { lang: "vi-VN", audioUrl: viAudioUrl(word.vietnamese) }), 2000)
      : null;
    return () => {
      clearTimeout(t1);
      if (t2) clearTimeout(t2);
    };
  }, [word?.id]);


  if (!word) return null;

  const playBilingual = () => {
    speak(word.english, { audioUrl: word.audioUrl, lang: "en-US" });
    if (word.vietnamese && word.vietnamese.trim()) {
      setTimeout(() => speak(word.vietnamese, { lang: "vi-VN", audioUrl: viAudioUrl(word.vietnamese) }), 1800);
    }
  };

  const next = () => {
    if (i + 1 >= words.length) onDone();
    else setI(i + 1);
  };

  return (
    <div className="intro-root">
      <div className="row-between" style={{ marginBottom: 12 }}>
        <span className="muted">
          {t("Word {n} of {total}", { n: i + 1, total: words.length })}
        </span>
        <button className="ghost" onClick={onDone}>
          {t("Skip ↠")}
        </button>
      </div>

      <div className="intro-card">
        <div className="intro-art-frame">
          <GameImage className="intro-image" src={word.imageUrl} alt={word.english} fallbackLabel="★" />
        </div>
        <button
          className="intro-speaker"
          onClick={playBilingual}
          aria-label={t("Replay audio")}
        >
          🔊
        </button>
        <div className="intro-english">{word.english}</div>
        {/* Helper translation under the target word. In English-learning
         *  mode this is the Vietnamese gloss; in Vietnamese-learning mode
         *  applyLearnLanguage has swapped the fields so it's the English
         *  helper. Either way, it teaches the meaning. */}
        {word.vietnamese && word.vietnamese !== word.english && (
          <div
            className="t-muted"
            style={{ marginTop: 6, fontWeight: 700, fontSize: "1.1rem", textAlign: "center" }}
          >
            {word.vietnamese}
          </div>
        )}
        {word.sentence && (
          <button
            className="intro-sentence"
            onClick={() => speak(word.sentence, { audioUrl: word.sentenceAudioUrl ?? null })}
            aria-label={t("Hear sentence")}
          >
            "{word.sentence}"
          </button>
        )}
      </div>

      <div className="intro-dots">
        {words.map((_, idx) => (
          <span key={idx} className={`intro-dot ${idx === i ? "active" : ""}`} />
        ))}
      </div>

      <button onClick={next} style={{ width: "100%", marginTop: 16 }}>
        {i + 1 >= words.length ? t("Start running →") : t("Next →")}
      </button>
    </div>
  );
}
